export type AnalysisSelectionMode = "unset" | "none" | "selected" | "use-default";

export interface AnalysisSelection {
	mode: AnalysisSelectionMode;
	taskIds?: string[];
}

// Metadata interfaces for preflight request
export interface UserInfo {
	name: string;
	email: string;
	phone: string;
	id: string;
	organizationName: string;
}

export interface PhoneNumber {
	id: string;
	organizationId: string;
	customerId: string;
	phoneNumber: string;
	phonenumberraw: string;
	label: string;
	createdAt: string;
	updatedAt: string;
}

export interface EmailAddress {
	id: string;
	organizationId: string;
	customerId: string;
	emailAddress: string;
	label: string;
	createdAt: string;
	updatedAt: string;
}

export interface Participant {
	id: string;
	userId: string | null;
	customerId: string;
	conversationId: string;
	websiteId: string;
	organizationId: string;
	notificationEmail: boolean;
	notificationSMS: boolean;
	email: string | null;
	phone: string;
	joinTime: string | null;
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
}

export interface Customer {
	name: string;
	id: string;
	firstname: string;
	lastname: string;
	companyName: string | null;
	organizationId: string;
	createdAt: string;
	updatedAt: string;
	createdBy: string;
	updatedBy: string | null;
	deletedBy: string | null;
	deletedAt: string | null;
	Participants: Participant;
	PhoneNumbers: PhoneNumber[];
	EmailAddresses: EmailAddress[];
}

export interface AnalysisMetadata {
	me: UserInfo;
	notes: string;
	customer: Customer;
	conversation?: { id: string; name: string; externalId: string };
	project?: { id: string; name: string; externalId: string };
	form?: {
		id: string;
		name: string;
		fields: Array<{ name: string; type: string; value: string }>;
	};
	gps?: {
		latitude: number;
		longitude: number;
		formattedAddress?: string;
		placeId?: string;
	};
}

export interface CreateAnalysisRequest {
	externalType: string;
	externalId: string;
	externalSource: string;
	files: ScribeFile[];
	packId: string;
	title?: string;
	metadata: AnalysisMetadata;
	selection?: AnalysisSelection;
	dryRun?: boolean;
	shareWith?: string;
	userInputData?: Record<string, Record<string, any>>;
}

export interface MediaConversionOptions {
	llmOptions: LLMOptions;
}

export interface LLMOptions {
	prompt: string;
	model: string;
}

export enum AnalysisStatus {
	QUEUED = "QUEUED",
	PENDING = "PENDING",
	RUNNING = "RUNNING",
	WAITING_FOR_INPUT = "WAITING_FOR_INPUT",
	COMPLETED = "COMPLETED",
	FAILED = "FAILED",
	CANCELLING = "CANCELLING",
	CANCELLED = "CANCELLED",
	NO_MEDIA = "NO_MEDIA"
}

export interface ScribeFile {
	externalId: string;
	externalType: string;
	externalSource: string;
	path: string;
	createdAt: Date;
	conversationId?: string;
	mimeType?: string;
}

export interface AnalysisCreateResponse {
	message: string;
	analysis: {
		id: string;
		externalType: string;
		externalSource: string;
		externalId: string;
		files: ScribeFile[];
		mediaConversionOptions: MediaConversionOptions;
		jobId: string;
		status: AnalysisStatus;
		createdAt: Date;
		updatedAt: Date;
	};
}

export interface AnalysisStatusResponse {
	total: number;
	data: AnalysisStatusResponseData[];
}

export interface AnalysisStatusResponseData {
	id: string;
	externalType: string;
	externalId: string;
	externalSource: string;
	packId?: string;
	title?: string;
	status: AnalysisStatus;
	isRealTime?: boolean;
	createdAt: Date;
	updatedAt: Date;
	mediaCompleteAt?: Date | string | null;
	deferredMedia?: boolean;
}

export interface AnalysisResult {
	id: string;
	externalType: string;
	externalId: string;
	externalSource: string;
	packId?: string;
	status: AnalysisStatus;
	createdAt: Date;
	updatedAt: Date;
	title: string;
	result: string;
	// Present when the report was generated against an output schema (V2 / structured-output reports).
	// We don't manipulate the shape on this side — its presence is what gates the V2 iframe viewer.
	structuredResult?: unknown | null;
	transcripts?: Transcript[];
	metadata?: AnalysisMetadata;
	mediaCompleteAt?: Date | string | null;
	deferredMedia?: boolean;
}

export interface Transcript {
	mediaId: string;
	mediaUrl: string;
	transcript: string;
	segments: TranscriptSegment[];
	createdAt: Date;
}

export interface TranscriptSegment {
	id: string;
	text: string;
	start: number;
	end: number;
	speaker: number;
}

// Platform authentication types
export interface PlatformInfo {
	codeName: string;
	productName: string;
	authenticationStatus: "Authenticated" | "NotAuthenticated" | "AuthenticatedButFailing";
}

export interface AvailablePlatformsResponse {
	platforms: PlatformInfo[];
	/** Set when Scribe served the user's last good list because Exchange was down (allowStale opt-in only). */
	stale?: boolean;
	fetchedAt?: string;
}

// Task selection types
export interface TaskSelection {
	id: string;
	packId: string;
	taskIds: string[];
	selectionState: string | null;
}

export interface TaskSelectionResponse {
	selection: TaskSelection;
}

export interface SaveTaskSelectionRequest {
	taskIds: string[];
	selectionState?: string;
}

export type OutputFormat = "csv" | "excel" | "pdf" | "text";

// Pack task types
export interface PackTask {
	id: string;
	name: string;
	description?: string;
	type: "media" | "platform" | "communication" | "presentation";
	platform?: string;
	platformName?: string;
	platformVendorName?: string;
	platformProductName?: string;
	prompt?: string;
	tools?: string[];
	order?: number;
	required?: boolean;
	outputFormats?: OutputFormat[] | null;
}

export interface Pack {
	id: string;
	name: string;
	description?: string;
	categoryId?: string;
	tasks: PackTask[];
	settingsFormConfig?: {
		fields: Array<Record<string, unknown>>;
		layout?: "vertical" | "horizontal";
	} | null;
}

export interface PackResponse {
	pack: Pack;
}

export interface PackListResponse {
	data: Pack[];
}

// Dry run response types
export interface DryRunSuccessResponse {
	message: "PreflightOk";
	selection: {
		mode: "use-default" | "selected" | "none";
		taskIds: string[];
	};
}

export interface DryRunErrorResponse {
	error: "PLATFORMS_NOT_AUTHENTICATED" | "SELECTION_REQUIRED";
	message: string;
	details?: {
		platforms?: string[];
	};
}

// Task result types for formatted outputs
export interface FormattedOutputFile {
	url: string;
	filename: string;
}

export interface FormattedOutputs {
	csv?: FormattedOutputFile[];
	excel?: FormattedOutputFile[];
	pdf?: FormattedOutputFile;
	text?: FormattedOutputFile;
}

export interface TaskResult {
	id: string;
	analysisId: string;
	taskId: string;
	taskName: string;
	formattedOutputs?: FormattedOutputs | null;
	status: string;
	createdAt: string;
	updatedAt: string;
}

export interface TaskResultsResponse {
	data: TaskResult[];
	total: number;
}

// Dry run validation response
export interface DryRunValidationResult {
	isValid: boolean;
	needsAuth: boolean;
	needsSelection: boolean;
	platforms: Array<{
		codeName: string;
		productName: string;
		authenticationStatus: "Authenticated" | "NotAuthenticated" | "AuthenticatedButFailing";
		required: boolean;
	}>;
	unauthenticatedPlatforms: Array<{
		codeName: string;
		productName: string;
		authenticationStatus: "NotAuthenticated" | "AuthenticatedButFailing";
		required: boolean;
	}>;
	tasks: Array<{
		id: string;
		name: string;
		description?: string;
		type: "media" | "platform" | "communication" | "presentation";
		platform?: string;
	}>;
	taskSelection: TaskSelection | null;
}
